// Libs
import axios, { type AxiosError, type AxiosInstance, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';

// Services
import CSessao from '../storage/CSessao';

// Router
import { irParaRotaLogin } from '@/router/NavegacaoUtils';

// Shared
import PWAUtils from '@/shared/utils/PWAUtils';
import { cENDPOINT_LOGIN_API, cENDPOINT_LOGIN_TEKSERVER } from '@/shared/constants/Constantes';

export type IQueryFiltro<T> = {
  campo: keyof T;
  valor: string | number;
};

type TTipoController = 'API_AGENDAMENTO' | 'API_TEKSERVER' | 'API_TEKMONITOR';

/**
 * Classe base para instâncias Axios.
 * Implementa um padrão Singleton por instância para evitar vazamento de memória com interceptors.
 */
export default abstract class CAxiosInstance {
  private readonly tipo: TTipoController;
  private _axiosInstance: AxiosInstance | null = null;

  constructor(pTipoController: TTipoController) {
    this.tipo = pTipoController;
  }

  // Métodos abstratos a serem implementados nas subclasses
  protected abstract getBaseUrl(): string;
  protected abstract getTokenAuth(): string;

  /**
   * Getter que gerencia a instância do Axios.
   * Garante que o axios.create e os interceptors sejam executados apenas uma vez.
   */
  protected get rest(): AxiosInstance {
    if (!this._axiosInstance) {
      this._axiosInstance = this.inicializar();
    }

    return this._axiosInstance;
  }

  private inicializar(): AxiosInstance {
    const api = axios.create({
      baseURL: this.getBaseUrl(),
      headers: {
        Authorization: this.getTokenAuth(),
        Accept: '*/*',
        'X-Requested-With': 'XMLHttpRequest',
        Plataforma: PWAUtils.obterPlataforma(),
      },
    });

    // --- Interceptor de Requisição ---
    api.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        // Atualiza o Token dinamicamente
        const lToken = this.getTokenAuth();

        if (lToken) {
          config.headers.Authorization = this.getTokenAuth();
        }

        return config;
      },
      (error: AxiosError) => Promise.reject(error),
    );

    // --- Interceptor de Resposta ---
    api.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        // Tratamento de falha de acesso (401)
        if (error.response?.status === 401) {
          // Gualter: Devido a uma questão de ordem de inicialização, não foram utilizadas as informações diretamente da classe controller (Solução mais elegante)
          const isLoginAPIRoute = error.config?.url?.includes(cENDPOINT_LOGIN_API);
          const isLoginTekServerRoute = error.config?.url?.includes(cENDPOINT_LOGIN_TEKSERVER);

          if (!isLoginAPIRoute && !isLoginTekServerRoute) {
            await CSessao.logout();
            setTimeout(() => {
              irParaRotaLogin();
            }, 3000);
          }
        }

        // Formatação do Erro conforme o Tipo de API
        const erroFormatado = this.formatarErroPorTipo(error);

        // Sempre retorna Promise.reject para que o erro possa ser capturado no .catch() ou try/catch
        return Promise.reject(erroFormatado);
      },
    );

    return api;
  }

  private formatarErroPorTipo(error: AxiosError): any {
    switch (this.tipo) {
      case 'API_AGENDAMENTO':
        return this.getErroAPIAgendamento(error);
      case 'API_TEKSERVER':
        return this.getErroAPITekServer(error);
      default:
        return error;
    }
  }

  // ---------------------------------------------
  // Tratamento de erro do TekServer
  // ---------------------------------------------
  protected getErroAPITekServer(pError: AxiosError): string {
    const mensagemGenerica =
      'Falha ao se conectar com o servidor. Por favor, verifique sua conexão e tente novamente mais tarde. Se o problema persistir, entre em contato com a empresa.';

    if (pError.code?.includes('ERR_NETWORK')) {
      return mensagemGenerica;
    }

    const response: any = pError.response?.data;
    if (!response.error) {
      return response;
    }
    const mensagem: string = response.error;

    if (mensagem.includes('>> MENSAGEM PERSONALIZADA DO SERVIDOR DE APLICAÇÃO <<')) {
      const substringDesejada: string = '>> MENSAGEM PERSONALIZADA DO SERVIDOR DE APLICAÇÃO <<';
      const posicaoInicio: number = mensagem.indexOf(substringDesejada);
      if (posicaoInicio !== -1) {
        const parteDaMensagem: string = mensagem.substring(posicaoInicio + substringDesejada.length);
        return parteDaMensagem.trim();
      }
      return mensagem;
    }

    if (mensagem.includes('>> MENSAGEM DO SERVIDOR DE APLICAÇÃO <<')) {
      const substringDesejada: string = '>> MENSAGEM DO SERVIDOR DE APLICAÇÃO <<';
      const posicaoInicio: number = mensagem.indexOf(substringDesejada);
      if (posicaoInicio !== -1) {
        const parteDaMensagem: string = mensagem.substring(posicaoInicio + substringDesejada.length);
        return parteDaMensagem.trim();
      }
      return mensagem;
    }

    return mensagem;
  }

  // ---------------------------------------------
  // Tratamento de erro da API de Agendamento
  // ---------------------------------------------
  protected getErroAPIAgendamento(pError: AxiosError): { tipo: string; erro: string; mensagem: string } {
    const mensagemGenerica =
      'Falha ao se conectar com o servidor. Por favor, verifique sua conexão e tente novamente mais tarde. Se o problema persistir, entre em contato com a empresa.';

    if (pError.code?.includes('ERR_NETWORK')) {
      return { tipo: 'FalhaConexão', erro: '', mensagem: mensagemGenerica };
    }

    const erro: any = pError.response?.data;

    if (erro.tipo && erro.mensagem && erro.mensagem) {
      return {
        tipo: erro.tipo,
        erro: erro.erro,
        mensagem: erro.mensagem,
      };
    }
    throw erro;
  }
}
